/* ============================================
   WikiComments — Article Comments Section
   Fetches, displays, creates, edits, and deletes
   comments. Uses supabase.from() directly.
   ============================================ */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

/* ── Types ── */
interface WikiComentario {
  id: string
  articulo_id: string
  anotacion_id: string | null
  user_id: string
  contenido: string
  parent_id: string | null
  created_at: string
  updated_at: string
}

/* ── Icons ── */
const icons = {
  messageCircle: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
    </svg>
  ),
  send: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  ),
  edit: (s = 14) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  trash: (s = 14) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
  ),
  check: (s = 14) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  x: (s = 14) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
}

interface WikiCommentsProps {
  articuloId: string
}

/* ── Avatar colors ── */
const AVATAR_COLORS = [
  '#4F7FE8', '#34d399', '#f472b6', '#fbbf24',
  '#a78bfa', '#f87171', '#60a5fa', '#22d3ee',
]

function getAvatarColor(userId: string): string {
  let hash = 0
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

function getInitial(userId: string): string {
  return userId.substring(0, 2).toUpperCase()
}

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Ahora'
  if (mins < 60) return `Hace ${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `Hace ${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 30) return `Hace ${days}d`
  return new Date(dateStr).toLocaleDateString('es-PE', { day: 'numeric', month: 'short' })
}

export default function WikiComments({ articuloId }: WikiCommentsProps) {
  const { user } = useAuth()
  const [comments, setComments] = useState<WikiComentario[]>([])
  const [loading, setLoading] = useState(true)
  const [newComment, setNewComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')

  /* ── Load comments ── */
  const loadComments = useCallback(async () => {
    setLoading(true)
    try {
      const { data, error } = await (supabase
        .from('wiki_comentarios') as any)
        .select('*')
        .eq('articulo_id', articuloId)
        .order('created_at', { ascending: true })

      if (error) throw error
      setComments((data || []) as WikiComentario[])
    } catch (err) {
      console.error('Error loading wiki comments:', err)
    }
    setLoading(false)
  }, [articuloId])

  useEffect(() => {
    loadComments()
  }, [loadComments])

  /* ── Create comment ── */
  const handleSubmit = async () => {
    if (!newComment.trim() || !user || submitting) return

    setSubmitting(true)
    try {
      const { error } = await (supabase
        .from('wiki_comentarios') as any)
        .insert({
          articulo_id: articuloId,
          user_id: user.id,
          contenido: newComment.trim(),
          anotacion_id: null,
          parent_id: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })

      if (error) throw error
      setNewComment('')
      loadComments()
    } catch (err) {
      console.error('Error creating comment:', err)
    }
    setSubmitting(false)
  }

  /* ── Edit comment ── */
  const handleEdit = async (id: string) => {
    if (!editText.trim()) return

    try {
      const { error } = await (supabase
        .from('wiki_comentarios') as any)
        .update({
          contenido: editText.trim(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)

      if (error) throw error
      setEditingId(null)
      setEditText('')
      loadComments()
    } catch (err) {
      console.error('Error updating comment:', err)
    }
  }

  /* ── Delete comment ── */
  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar este comentario?')) return

    try {
      const { error } = await (supabase
        .from('wiki_comentarios') as any)
        .delete()
        .eq('id', id)

      if (error) throw error
      loadComments()
    } catch (err) {
      console.error('Error deleting comment:', err)
    }
  }

  return (
    <section className="wiki-comments">
      {/* ── Header ── */}
      <div className="wiki-comments__header">
        <h3 className="wiki-comments__title">
          {icons.messageCircle()} Comentarios
          {comments.length > 0 && (
            <span className="wiki-comments__count">{comments.length}</span>
          )}
        </h3>
      </div>

      {/* ── Comment Form ── */}
      <div className="wiki-comments__form">
        {user && (
          <div className="wiki-comments__form-row">
            <div
              className="wiki-comments__avatar"
              style={{ background: getAvatarColor(String(user.id)) }}
            >
              {getInitial(user.email || String(user.id))}
            </div>
            <div className="wiki-comments__form-input-wrap">
              <textarea
                className="wiki-comments__textarea"
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                placeholder="Escribe un comentario..."
                rows={2}
                onKeyDown={e => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    handleSubmit()
                  }
                }}
              />
              <button
                className="wiki-comments__submit-btn"
                onClick={handleSubmit}
                disabled={!newComment.trim() || submitting}
                title="Enviar (Ctrl+Enter)"
              >
                {icons.send()} {submitting ? 'Enviando...' : 'Comentar'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Comment List ── */}
      <div className="wiki-comments__list">
        {loading ? (
          <div className="wiki-comments__loading">Cargando comentarios...</div>
        ) : comments.length === 0 ? (
          <div className="wiki-comments__empty">
            Sé el primero en comentar este artículo
          </div>
        ) : (
          comments.map(comment => {
            const isOwn = String(user?.id) === comment.user_id
            const isEditing = editingId === comment.id

            return (
              <div key={comment.id} className="wiki-comments__item">
                <div
                  className="wiki-comments__avatar"
                  style={{ background: getAvatarColor(comment.user_id) }}
                >
                  {getInitial(comment.user_id)}
                </div>
                <div className="wiki-comments__content">
                  <div className="wiki-comments__item-header">
                    <span className="wiki-comments__author">
                      {comment.user_id.substring(0, 8)}...
                    </span>
                    <span className="wiki-comments__time">
                      {formatTimeAgo(comment.created_at)}
                    </span>
                    {isOwn && !isEditing && (
                      <div className="wiki-comments__item-actions">
                        <button
                          className="wiki-comments__action-btn"
                          onClick={() => {
                            setEditingId(comment.id)
                            setEditText(comment.contenido)
                          }}
                          title="Editar"
                        >
                          {icons.edit()}
                        </button>
                        <button
                          className="wiki-comments__action-btn wiki-comments__action-btn--danger"
                          onClick={() => handleDelete(comment.id)}
                          title="Eliminar"
                        >
                          {icons.trash()}
                        </button>
                      </div>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="wiki-comments__edit-form">
                      <textarea
                        className="wiki-comments__textarea"
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                        rows={2}
                        autoFocus
                      />
                      <div className="wiki-comments__edit-actions">
                        <button
                          className="wiki-comments__action-btn"
                          onClick={() => { setEditingId(null); setEditText('') }}
                          title="Cancelar"
                        >
                          {icons.x()} Cancelar
                        </button>
                        <button
                          className="wiki-comments__action-btn wiki-comments__action-btn--primary"
                          onClick={() => handleEdit(comment.id)}
                          title="Guardar"
                        >
                          {icons.check()} Guardar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="wiki-comments__text">{comment.contenido}</p>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>
    </section>
  )
}
